import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useAppSelector } from '../../Common/_hooks/useAppSelector';
import SeoLinks, { type AppLocale, buildAbsoluteSeoUrl, getCanonicalLocale, getI18nextLocale, joinSeoPath } from '../../Common/SEO/SeoLinks';
import StructuredData, {
  buildBreadcrumbListSchema,
  getSeoHomeName,
  getSeoProductsName,
  type SeoListItem,
} from '../../Common/SEO/StructuredData';
import useProductCatalogFiltering, { FAVORITES_SEARCH_PARAM, PRODUCT_SEARCH_PARAM } from '../hooks/useProductCatalogFiltering';
import type ViewCategoryStore from '../../AppData/_stores/ViewCategoryStore';

interface ProductCatalogHeadProps {
  description: string
  title: string
}

const getLocalizedCategorySlug = (category: ViewCategoryStore, locale: AppLocale) => {
  return category.get('url_slugs').get(locale) ?? category.get('url_slug');
}

export default function ProductCatalogHead(props: ProductCatalogHeadProps) {
  const [t] = useTranslation('products');
  const [, routesI18n] = useTranslation('routes');
  const [searchParams] = useSearchParams();
  const viewProductAttributeTypes = useAppSelector(state => state.get('appData').get('viewProductAttributeTypes'));
  const currentLocale = getCanonicalLocale(routesI18n.resolvedLanguage ?? routesI18n.language);
  const {
    category1,
    category2,
    selectedCollection,
  } = useProductCatalogFiltering();

  const catalogSeoPaths = useMemo(() => {
    const buildCatalogPath = (locale: AppLocale) => {
      const productBasePath = routesI18n.getFixedT(getI18nextLocale(locale), 'routes')('/products');

      if (selectedCollection !== undefined) {
        return joinSeoPath(productBasePath, selectedCollection.slug);
      }

      const category1Slug = category1?.get('url_slugs').get(locale) ?? category1?.get('url_slug');
      const category2Slug = category2?.get('url_slugs').get(locale) ?? category2?.get('url_slug');

      return joinSeoPath(productBasePath, category1Slug, category2Slug);
    }

    return {
      en_CA: buildCatalogPath('en_CA'),
      fr_CA: buildCatalogPath('fr_CA'),
    }
  }, [category1, category2, routesI18n, selectedCollection])

  const shouldNoIndexCatalog = useMemo(() => {
    if (searchParams.has(PRODUCT_SEARCH_PARAM) || searchParams.has(FAVORITES_SEARCH_PARAM)) {
      return true;
    }

    return viewProductAttributeTypes.some(type => searchParams.has(type.get('slug')));
  }, [searchParams, viewProductAttributeTypes])

  const catalogBreadcrumbSchema = useMemo(() => {
    if (shouldNoIndexCatalog) return null;

    const fixedRoutes = routesI18n.getFixedT(getI18nextLocale(currentLocale), 'routes');
    const productBasePath = fixedRoutes('/products');
    const breadcrumbItems: SeoListItem[] = [
      {
        name: getSeoHomeName(currentLocale),
        url: buildAbsoluteSeoUrl(fixedRoutes('/')),
      },
      {
        name: getSeoProductsName(currentLocale),
        url: buildAbsoluteSeoUrl(productBasePath),
      },
    ];

    if (selectedCollection !== undefined) {
      breadcrumbItems.push({
        name: selectedCollection.title(t),
        url: buildAbsoluteSeoUrl(catalogSeoPaths[currentLocale]),
      });

      return buildBreadcrumbListSchema(breadcrumbItems);
    }

    const categoryPath: string[] = [];
    [category1, category2].forEach(category => {
      if (category === undefined) return;

      categoryPath.push(getLocalizedCategorySlug(category, currentLocale));
      breadcrumbItems.push({
        name: category.get('name'),
        url: buildAbsoluteSeoUrl(joinSeoPath(productBasePath, ...categoryPath)),
      });
    });

    return buildBreadcrumbListSchema(breadcrumbItems);
  }, [catalogSeoPaths, category1, category2, currentLocale, routesI18n, selectedCollection, shouldNoIndexCatalog, t])

  return <>
    <SeoLinks
      canonicalPath={catalogSeoPaths[currentLocale]}
      alternatePaths={catalogSeoPaths}
      noIndex={shouldNoIndexCatalog}
    />
    <Helmet>
      <title>{t('{{title}} – Print-on-demand', { title: props.title })}</title>
      <meta name="description" content={props.description} />
    </Helmet>
    <StructuredData data={catalogBreadcrumbSchema} />
  </>
}
